import { useState } from 'react';

interface VocabItem {
  term: string;
  definition: string;
}

interface Example {
  problem: string;
  steps: string[];
}

interface Question {
  id: number;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

interface Lesson {
  id: number;
  title: string;
  standards: string[];
  vocabulary: VocabItem[];
  conceptExplanation: string;
  examples: Example[];
  teacherTip: string;
  questions: Question[];
}

interface LessonCardProps {
  lesson: Lesson;
}

export default function LessonCard({ lesson }: LessonCardProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'practice'>('overview');
  const [expandedExamples, setExpandedExamples] = useState<Record<number, number>>({});
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number>>({});
  const [submitted, setSubmitted] = useState(false);
  const [filter, setFilter] = useState<'all' | 'correct' | 'incorrect'>('all');
  const [showSolutions, setShowSolutions] = useState<Record<number, boolean>>({});

  const revealNextStep = (exampleIndex: number) => {
    setExpandedExamples(prev => ({
      ...prev,
      [exampleIndex]: (prev[exampleIndex] || 0) + 1,
    }));
  };

  const resetExample = (exampleIndex: number) => {
    setExpandedExamples(prev => ({ ...prev, [exampleIndex]: 0 }));
  };

  const handleAnswerSelect = (questionId: number, answerIndex: number) => {
    if (!submitted) {
      setSelectedAnswers(prev => ({ ...prev, [questionId]: answerIndex }));
    }
  };

  const handleSubmit = () => setSubmitted(true);

  const handleReset = () => {
    setSelectedAnswers({});
    setShowSolutions({});
    setSubmitted(false);
    setFilter('all');
  };

  const toggleSolution = (questionId: number) => {
    setShowSolutions(prev => ({ ...prev, [questionId]: !prev[questionId] }));
  };

  const showAllSolutions = () => {
    const all: Record<number, boolean> = {};
    lesson.questions.forEach(q => { all[q.id] = true; });
    setShowSolutions(all);
  };

  const score = lesson.questions.reduce((acc, q) => {
    return acc + (selectedAnswers[q.id] === q.correctAnswer ? 1 : 0);
  }, 0);

  const answeredCount = Object.keys(selectedAnswers).length;

  const filteredQuestions = lesson.questions.filter(q => {
    if (filter === 'correct') return selectedAnswers[q.id] === q.correctAnswer;
    if (filter === 'incorrect') return selectedAnswers[q.id] !== undefined && selectedAnswers[q.id] !== q.correctAnswer;
    return true;
  });

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
      {/* Card Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-violet-600 p-6 text-white">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0 text-xl font-bold">
            {lesson.id}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-bold mb-1 leading-snug">{lesson.title}</h3>
            <div className="flex flex-wrap gap-1 mt-2">
              {lesson.standards.slice(0, 2).map((std, i) => (
                <span key={i} className="text-xs bg-white/20 px-2 py-0.5 rounded-full font-medium truncate max-w-xs">
                  {std.split('.').slice(-1)[0] || std}
                </span>
              ))}
              {lesson.standards.length > 2 && (
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full font-medium">
                  +{lesson.standards.length - 2} more
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('overview')}
          className={`flex-1 px-4 py-3 text-sm font-semibold transition-colors whitespace-nowrap ${
            activeTab === 'overview'
              ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50'
              : 'text-gray-500 hover:text-indigo-600 hover:bg-gray-50'
          }`}
        >
          <i className="ri-book-open-line mr-1"></i>
          Lesson
        </button>
        <button
          onClick={() => setActiveTab('practice')}
          className={`flex-1 px-4 py-3 text-sm font-semibold transition-colors whitespace-nowrap ${
            activeTab === 'practice'
              ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50'
              : 'text-gray-500 hover:text-indigo-600 hover:bg-gray-50'
          }`}
        >
          <i className="ri-pencil-line mr-1"></i>
          Practice ({lesson.questions.length})
        </button>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Vocabulary */}
            <div>
              <h4 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-bookmark-line text-indigo-500"></i>
                Key Vocabulary
              </h4>
              <div className="flex flex-wrap gap-2">
                {lesson.vocabulary.map((item, i) => (
                  <div key={i} className="group relative">
                    <span className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm font-medium cursor-help border border-indigo-100 hover:bg-indigo-100 transition-colors">
                      {item.term}
                    </span>
                    <div className="absolute bottom-full left-0 mb-2 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 hidden group-hover:block z-10 shadow-xl leading-relaxed">
                      <strong className="text-indigo-300">{item.term}:</strong> {item.definition}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Concept Explanation */}
            <div>
              <h4 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-lightbulb-line text-indigo-500"></i>
                Concept Explanation
              </h4>
              <div className="bg-gradient-to-br from-indigo-50 to-violet-50 rounded-xl p-5 border border-indigo-100">
                <p className="text-gray-700 leading-relaxed whitespace-pre-line text-sm">
                  {lesson.conceptExplanation}
                </p>
              </div>
            </div>

            {/* Worked Examples */}
            <div>
              <h4 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-file-list-3-line text-indigo-500"></i>
                Worked Examples
              </h4>
              <div className="space-y-4">
                {lesson.examples.map((example, exIdx) => {
                  const revealed = expandedExamples[exIdx] || 0;
                  return (
                    <div key={exIdx} className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                      <div className="flex items-start gap-3 mb-4">
                        <span className="w-7 h-7 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {exIdx + 1}
                        </span>
                        <p className="text-gray-900 font-semibold text-sm leading-relaxed">{example.problem}</p>
                      </div>
                      <div className="ml-10 space-y-2 mb-4">
                        {example.steps.map((step, sIdx) => (
                          <div
                            key={sIdx}
                            className={`pl-3 border-l-2 border-indigo-400 py-1 text-sm text-gray-700 transition-all duration-300 ${
                              sIdx < revealed ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden pointer-events-none'
                            }`}
                          >
                            {step}
                          </div>
                        ))}
                      </div>
                      <div className="ml-10 flex gap-2">
                        {revealed < example.steps.length && (
                          <button
                            onClick={() => revealNextStep(exIdx)}
                            className="text-xs bg-indigo-600 text-white px-4 py-1.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors whitespace-nowrap"
                          >
                            <i className="ri-arrow-right-line mr-1"></i>
                            Next Step
                          </button>
                        )}
                        {revealed === example.steps.length && (
                          <span className="text-xs text-green-600 font-semibold flex items-center gap-1">
                            <i className="ri-check-line"></i> Complete!
                          </span>
                        )}
                        {revealed > 0 && (
                          <button
                            onClick={() => resetExample(exIdx)}
                            className="text-xs bg-gray-200 text-gray-600 px-4 py-1.5 rounded-lg font-semibold hover:bg-gray-300 transition-colors whitespace-nowrap"
                          >
                            Reset
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Teacher Tip */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-5 border border-amber-200">
              <div className="flex items-start gap-3">
                <i className="ri-user-smile-line text-amber-500 text-xl flex-shrink-0 mt-0.5"></i>
                <div>
                  <h4 className="font-bold text-amber-900 mb-1 text-sm">Mr. Augustine's Teaching Tip</h4>
                  <p className="text-amber-800 text-sm leading-relaxed">{lesson.teacherTip}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'practice' && (
          <div>
            {!submitted && (
              <div className="mb-5 bg-indigo-50 rounded-xl p-4 border border-indigo-200">
                <p className="text-indigo-800 text-sm font-medium">
                  <i className="ri-information-line mr-1"></i>
                  Answer all {lesson.questions.length} questions, then click Submit to see your score and solutions.
                </p>
                <div className="mt-2 bg-indigo-200 rounded-full h-1.5">
                  <div
                    className="bg-indigo-600 h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${(answeredCount / lesson.questions.length) * 100}%` }}
                  />
                </div>
                <p className="text-xs text-indigo-600 mt-1">{answeredCount} / {lesson.questions.length} answered</p>
              </div>
            )}

            {submitted && (
              <div className="mb-5 space-y-3">
                <div className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-xl p-5 text-white flex items-center justify-between">
                  <div>
                    <p className="text-lg font-bold">Score: {score} / {lesson.questions.length}</p>
                    <p className="text-indigo-100 text-sm">{Math.round((score / lesson.questions.length) * 100)}% Correct</p>
                  </div>
                  <div className="text-4xl font-bold opacity-90">
                    {Math.round((score / lesson.questions.length) * 100)}%
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(['all', 'correct', 'incorrect'] as const).map(f => (
                    <button
                      key={f}
                      onClick={() => setFilter(f)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap ${
                        filter === f
                          ? f === 'correct' ? 'bg-green-600 text-white'
                            : f === 'incorrect' ? 'bg-red-600 text-white'
                            : 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {f === 'all' ? `All (${lesson.questions.length})` : f === 'correct' ? `Correct (${score})` : `Incorrect (${lesson.questions.length - score})`}
                    </button>
                  ))}
                  <button
                    onClick={showAllSolutions}
                    className="px-3 py-1.5 bg-violet-600 text-white rounded-lg text-sm font-semibold hover:bg-violet-700 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-eye-line mr-1"></i>Show All
                  </button>
                  <button
                    onClick={handleReset}
                    className="px-3 py-1.5 bg-gray-600 text-white rounded-lg text-sm font-semibold hover:bg-gray-700 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-refresh-line mr-1"></i>Retake
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {filteredQuestions.map(q => {
                const isCorrect = selectedAnswers[q.id] === q.correctAnswer;
                const isAnswered = selectedAnswers[q.id] !== undefined;
                return (
                  <div
                    key={q.id}
                    className={`rounded-xl border-2 p-4 transition-all ${
                      submitted && isAnswered
                        ? isCorrect ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <span className="w-7 h-7 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {q.id}
                      </span>
                      <p className="text-gray-900 font-medium text-sm flex-1 leading-relaxed">{q.question}</p>
                      {submitted && isAnswered && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-bold whitespace-nowrap flex-shrink-0 ${
                          isCorrect ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
                        }`}>
                          {isCorrect ? '✓ Correct' : '✗ Wrong'}
                        </span>
                      )}
                    </div>
                    <div className="ml-10 space-y-2">
                      {q.options.map((opt, oIdx) => (
                        <label
                          key={oIdx}
                          className={`flex items-center gap-2 p-2.5 rounded-lg border-2 cursor-pointer transition-all text-sm ${
                            selectedAnswers[q.id] === oIdx
                              ? submitted
                                ? oIdx === q.correctAnswer
                                  ? 'border-green-500 bg-green-100'
                                  : 'border-red-500 bg-red-100'
                                : 'border-indigo-500 bg-indigo-50'
                              : submitted && oIdx === q.correctAnswer
                              ? 'border-green-400 bg-green-50'
                              : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="radio"
                            name={`q-${q.id}`}
                            value={oIdx}
                            checked={selectedAnswers[q.id] === oIdx}
                            onChange={() => handleAnswerSelect(q.id, oIdx)}
                            disabled={submitted}
                            className="w-3.5 h-3.5 text-indigo-600 cursor-pointer"
                          />
                          <span className="text-gray-800">{opt}</span>
                        </label>
                      ))}
                    </div>
                    {submitted && (
                      <div className="ml-10 mt-3">
                        <button
                          onClick={() => toggleSolution(q.id)}
                          className="text-indigo-600 hover:text-indigo-800 text-xs font-semibold flex items-center gap-1 transition-colors"
                        >
                          <i className={showSolutions[q.id] ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                          {showSolutions[q.id] ? 'Hide' : 'Show'} Explanation
                        </button>
                        {showSolutions[q.id] && (
                          <div className="mt-2 p-3 bg-white rounded-lg border border-gray-200 text-xs text-gray-700 leading-relaxed">
                            <span className="font-bold text-gray-900">Answer: {q.options[q.correctAnswer]} — </span>
                            {q.explanation}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {!submitted && (
              <div className="mt-6 flex justify-center">
                <button
                  onClick={handleSubmit}
                  disabled={answeredCount !== lesson.questions.length}
                  className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-xl font-bold hover:from-indigo-700 hover:to-violet-700 transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap shadow-lg"
                >
                  <i className="ri-check-double-line mr-2"></i>
                  Submit Answers
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
